import React, { useState } from "react";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import { useLazyListLocalTransportRuleQuery } from "store/services/LocalTransportRuleApi";
import { toast } from "react-toastify";
import moment from "moment";

const ExportLocalTransportRule = ({ filters }) => {
    const [isExporting, setIsExporting] = useState(false);
    const [triggerExport] = useLazyListLocalTransportRuleQuery();

    const handleExport = async () => {
        try {
            setIsExporting(true);

            const queryParams = {
                active: filters.active,
                keyword: filters.keyword || "",
                branch: filters.branch || "",
                toBranch: filters.toBranch || "",
                limit: 10000, // Large limit for export
                page: 1,
            };

            const response = await triggerExport(queryParams).unwrap();

            // Check various possible data structures
            let exportData = null;

            if (response?.data?.data) {
                // Structure: { data: { data: [...] } }
                exportData = response.data.data;
            } else if (response?.data) {
                // Structure: { data: [...] }
                exportData = response.data;
            } else if (Array.isArray(response)) {
                // Structure: [...]
                exportData = response;
            }

            console.log("Response:", response);
            console.log("Export Data:", exportData);

            if (!exportData || !Array.isArray(exportData) || exportData.length === 0) {
                toast.info("No data to export");
                return;
            }

            const csvContent = generateCSV(exportData);
            downloadCSV(
                csvContent,
                `local-transport-rules-${moment().format("YYYY-MM-DD")}.csv`
            );

            toast.success(`Exported ${exportData.length} records successfully`);
        } catch (error) {
            console.error("Export failed:", error);
            toast.error("Export failed: " + (error?.message || "Unknown error"));
        } finally {
            setIsExporting(false);
        }
    };

    const generateCSV = (data) => {
        const headers = [
            "Entity",
            "From Branch",
            "To Branch",
            "Mode",
            "Max Dist (km)",
            "Max Cost",
            "Price Per Sack",
            "Description",
            "Created By",
            "Created At",
            "Status",
        ];

        const rows = data.map((row) => {
            const fromBranches = Array.isArray(row.branch)
                ? row.branch.map((b) => b?.name).filter(Boolean).join("; ")
                : row.branch?.name || "";

            const toBranches = Array.isArray(row.toBranch)
                ? row.toBranch.map((b) => b?.name).filter(Boolean).join("; ")
                : row.toBranch?.name || "";

            return [
                `"${row.entity?.name || ""}"`,
                `"${fromBranches}"`,
                `"${toBranches}"`,
                `"${row.transportationMode?.name || ""}"`,
                row.maxDistanceKm || "",
                row.maxCost || "",
                row.pricePerSack || "",
                `"${(row.description || "").replace(/"/g, '""')}"`, // Escape quotes
                `"${row.createdBy?.name || ""}"`,
                row.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm") : "",
                row.isActive ? "Active" : "Inactive",
            ].join(",");
        });

        return [headers.join(","), ...rows].join("\n");
    };

    const downloadCSV = (content, fileName) => {
        try {
            // Add BOM for proper UTF-8 encoding in Excel
            const BOM = "\uFEFF";
            const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");

            if (navigator.msSaveBlob) {
                // IE 10+
                navigator.msSaveBlob(blob, fileName);
            } else {
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", fileName);
                link.style.visibility = "hidden";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the URL object
                setTimeout(() => URL.revokeObjectURL(url), 100);
            }
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Failed to download file");
        }
    };

    return (
        <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={handleExport}
            disabled={isExporting}
            size="small"
        >
            {isExporting ? "Exporting..." : "Export"}
        </Button>
    );
};

export default ExportLocalTransportRule;